import { useCallback, useEffect, useRef, useState } from "react"

import CloseIconV2 from "@/assets/img/modal/close_icon_v2.svg"
import GeneralBg from "@/assets/img/modal/general_background.webp"
import BackgroundImage from "@/assets/img/live-chat/stars_background.webp"
import checkmarkIcon from "@/assets/img/review/checkmark.svg"
import reportFasterImage from "@/assets/img/review/image_342.webp"
import Star from "@/assets/img/review/star.svg"
import StarPath from "@/assets/img/review/star_path.svg"
import trendingIcon from "@/assets/img/review/trending.webp"
import { env } from "@/config/env"
import { ANALYTICS_EVENTS, trackEvent } from "@/features/analytics"
import { auth } from "@/features/auth/providers/firebase"
import {
  remainingOnboardingLockMs,
  skipOnboardingLock,
  useOnboardingLockStore,
} from "@/features/onboarding-lock"
import {
  applyIncludedUnlocksAfterLikedReview,
  formatUnlockLabel,
  INCLUDED_UNLOCK_REPORT_IDS,
  peekUserReports,
  UNLOCK_DURATION_MS,
  unlockRemainingMs,
  useReportUnlockStore,
} from "@/features/today"
import { useAuthStore } from "@/stores/auth-store"

import { sendReview } from "../api/send-review"
import type { AppModalComponentProps } from "../config/modal-registry"
import { markReviewModalShown } from "../persistence/review-modal-seen"

/** Longest active gate: quiz onboarding lock or included Soulmate/Astro Map unlock. */
function useReadyAsUsualRemainingMs(enabled: boolean): number {
  const onboardingStartedAt = useOnboardingLockStore((s) => s.startedAt)
  const onboardingSkipped = useOnboardingLockStore((s) => s.skipped)
  const unlockStartedAt = useReportUnlockStore((s) => s.startedAt)
  const unlockDurationMs = useReportUnlockStore((s) => s.durationMs)
  const [now, setNow] = useState(() => Date.now())

  const onboardingRemaining = remainingOnboardingLockMs(
    onboardingStartedAt,
    onboardingSkipped,
    now,
  )
  const includedRemaining = Math.max(
    0,
    ...INCLUDED_UNLOCK_REPORT_IDS.map((id) =>
      unlockRemainingMs(unlockStartedAt[id], now, unlockDurationMs[id] ?? UNLOCK_DURATION_MS),
    ),
  )
  const remainingMs = Math.max(onboardingRemaining, includedRemaining)

  useEffect(() => {
    if (!enabled || remainingMs <= 0) return
    const id = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [enabled, remainingMs])

  return remainingMs
}

const CTA_CLASS =
  "relative z-[1] flex h-14 w-full shrink-0 items-center justify-center rounded-[80px] bg-gradient-to-b from-peach to-purple-accent font-body text-base font-semibold text-white disabled:bg-none disabled:bg-purple-light/10 disabled:text-purple-light/70"

/**
 * Rating request sheet — shown once when the user leaves an advisor chat.
 * Leave a review: skips onboarding lock; included Soulmate/Astro Map unlock
 * immediately if API-ready, else shorten 30‑min → 10‑min.
 */
export function ReviewModal({ onClose }: AppModalComponentProps) {
  const user = useAuthStore((s) => s.user)
  const [stars, setStars] = useState<number | null>(null)
  const [feedback, setFeedback] = useState("")
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const [loading, setLoading] = useState(false)
  const [leavingReview, setLeavingReview] = useState(false)
  const [sharedState, setSharedState] = useState<
    "liked" | "not_liked" | "feedback_sended" | null
  >(null)
  const readyAsUsualRemainingMs = useReadyAsUsualRemainingMs(sharedState === "liked")
  const readyAsUsualLabel = formatUnlockLabel(readyAsUsualRemainingMs)
  const fasterUnlockLabel = "Now"

  const baseParams = {
    userId: user?.uid ?? auth.currentUser?.uid ?? "",
  }

  useEffect(() => {
    trackEvent(ANALYTICS_EVENTS.REVIEW_MODAL_SHOWN, {
      ...baseParams,
      metadata: {},
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const closeReviewModal = (method: "close_icon" | "continue") => {
    trackEvent(ANALYTICS_EVENTS.REVIEW_MODAL_CLOSED, {
      ...baseParams,
      metadata: {
        rating: stars,
        state: sharedState ?? "rating",
        method,
      },
    })
    markReviewModalShown()
    onClose()
  }

  const handleShareRating = useCallback(() => {
    if (stars === null) return
    const sentiment = stars > 3 ? "liked" : "not_liked"
    trackEvent(ANALYTICS_EVENTS.REVIEW_MODAL_RATING_SUBMITTED, {
      ...baseParams,
      metadata: { rating: stars, sentiment },
    })
    if (stars > 3) {
      setSharedState("liked")
    } else {
      setSharedState("not_liked")
      setTimeout(() => {
        inputRef.current?.focus()
      }, 200)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stars])

  const handleLeaveReviewClick = async () => {
    if (leavingReview) return
    setLeavingReview(true)
    trackEvent(ANALYTICS_EVENTS.REVIEW_MODAL_LEAVE_REVIEW_CLICK, {
      ...baseParams,
      metadata: { rating: stars },
    })
    skipOnboardingLock()
    window.open(env.VITE_REVIEW_URL, "_blank")
    try {
      // Peek (no shared-cache write) so OTP timers like Love Match stay intact.
      applyIncludedUnlocksAfterLikedReview(await peekUserReports())
    } catch {
      /* leave included timers as-is */
    }
    closeReviewModal("continue")
  }

  const handleSendFeedbackClick = async () => {
    const firebaseUser = auth.currentUser
    if (!feedback || stars === null || !firebaseUser) return

    setLoading(true)
    try {
      const projectId =
        auth.app.options.projectId ||
        env.VITE_FIREBASE_AUTH_DOMAIN.replace(".firebaseapp.com", "") ||
        "lpr4-astrology"

      await sendReview({
        user_id: firebaseUser.uid,
        project_id: projectId,
        name: firebaseUser.displayName ?? "",
        review: feedback,
        rating: stars,
      })

      trackEvent(ANALYTICS_EVENTS.REVIEW_MODAL_FEEDBACK_SUBMITTED, {
        ...baseParams,
        metadata: { rating: stars },
      })

      setSharedState("feedback_sended")
    } catch (error) {
      console.error("Failed to send review:", error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      data-slot="review-modal"
      className="fixed inset-0 z-[1200] mx-auto flex min-h-[100dvh] w-full max-w-app flex-col justify-end bg-dark-base/80 backdrop-blur-[10px]"
    >
      <div className="relative flex w-full flex-col items-center gap-4 overflow-hidden rounded-t-3xl bg-dark-surface px-4 pb-4 pt-6">
        {sharedState === null ? (
          <img
            src={BackgroundImage}
            alt=""
            aria-hidden
            className="pointer-events-none absolute left-0 top-0 select-none"
          />
        ) : (
          <img
            src={GeneralBg}
            alt=""
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-0 h-60 w-full select-none object-cover"
          />
        )}

        {sharedState !== null && sharedState !== "feedback_sended" ? (
          <button
            type="button"
            onClick={() => closeReviewModal("close_icon")}
            aria-label="Close"
            className="absolute right-4 top-4 z-10 flex size-6 items-center justify-center"
          >
            <img src={CloseIconV2} alt="" className="size-6" />
          </button>
        ) : null}

        {sharedState === null ? (
          <>
            <div className="relative z-[1] flex w-full flex-col items-center gap-2 pt-[132px]">
              <h2 className="w-full text-center font-display text-2xl font-medium text-purple-light">
                Worth 5 stars?
              </h2>
              <p className="w-full text-center font-body text-base text-purple-light/70">
                Join thousands who trust Live Palmistry to reveal what the stars hold
                for you
              </p>
              <div className="mt-2 flex gap-2">
                {[1, 2, 3, 4, 5].map((el) => (
                  <button
                    key={el}
                    type="button"
                    onClick={() => setStars(el)}
                    className="cursor-pointer"
                    aria-label={`${el} star${el > 1 ? "s" : ""}`}
                  >
                    <img
                      src={el - 1 < (stars ?? 0) ? Star : StarPath}
                      alt=""
                      className="h-[42px] w-[45px] min-h-[42px] min-w-[45px]"
                    />
                  </button>
                ))}
              </div>
            </div>
            <button
              type="button"
              disabled={stars === null}
              onClick={handleShareRating}
              className={CTA_CLASS}
            >
              Share your rating
            </button>
          </>
        ) : null}

        {sharedState === "liked" ? (
          <>
            <div className="relative z-[1] flex w-full flex-col items-center gap-4 pt-2">
              <h2 className="w-full text-center font-display text-2xl font-medium text-purple-light">
                Glad you liked it!
              </h2>
              <div className="flex w-full flex-col items-center gap-3 overflow-hidden rounded-3xl border border-purple-light/10 bg-dark-base p-4 shadow-[0_0_16px_rgba(255,83,127,0.2),0_0_32px_#9565fd]">
                <img
                  src={reportFasterImage}
                  alt=""
                  className="h-[93px] w-[110px] object-cover"
                />
                <p className="text-center font-display text-xl font-medium text-purple-light">
                  Get your report
                  <br />
                  faster with a quick review
                </p>
                <div className="flex w-full items-center justify-center gap-2">
                  <div className="flex min-w-0 flex-1 flex-col items-center rounded-[20px] bg-purple-light/10 py-2 text-center">
                    <span className="font-body text-sm text-purple-light/70">
                      Ready as usual
                    </span>
                    <span className="whitespace-nowrap font-display text-xl font-medium leading-6 text-purple-light">
                      {readyAsUsualLabel}
                    </span>
                  </div>
                  <span className="shrink-0 font-body text-sm text-purple-light/70">
                    or
                  </span>
                  <div className="flex min-w-0 flex-1 flex-col items-center rounded-[20px] bg-purple-light/10 py-2">
                    <span className="text-center font-body text-sm text-purple-light/70">
                      Rate & unlock
                    </span>
                    <div className="flex items-center gap-1">
                      <img
                        src={trendingIcon}
                        alt=""
                        className="h-5 w-[15px] object-contain"
                      />
                      <span className="whitespace-nowrap font-display text-xl font-medium leading-6 text-purple-light">
                        {fasterUnlockLabel}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <button
              type="button"
              disabled={leavingReview}
              onClick={() => void handleLeaveReviewClick()}
              className={CTA_CLASS}
            >
              Leave a review
            </button>
          </>
        ) : null}

        {sharedState === "not_liked" ? (
          <>
            <div className="relative z-[1] flex w-full flex-col items-center gap-4 pt-2">
              <h2 className="w-full text-center font-display text-2xl font-medium text-purple-light">
                Thanks for sharing
              </h2>
              <textarea
                ref={inputRef}
                autoFocus
                rows={5}
                placeholder="Write your review"
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                className="h-[100px] w-full resize-none rounded-[20px] border border-purple-light/10 bg-dark-base/40 p-4 font-body text-base text-purple-light caret-main-purple outline-none placeholder:text-purple-light/50 focus:border-purple-light/20"
              />
            </div>
            <button
              type="button"
              disabled={feedback === "" || loading}
              onClick={() => void handleSendFeedbackClick()}
              className={CTA_CLASS}
            >
              {loading ? (
                <>
                  <span className="mr-2 size-5 animate-spin rounded-full border-2 border-purple-light border-t-transparent" />
                  Sending...
                </>
              ) : (
                "Send feedback"
              )}
            </button>
          </>
        ) : null}

        {sharedState === "feedback_sended" ? (
          <>
            <div className="relative z-[1] flex w-full flex-col items-center gap-2">
              <img
                src={checkmarkIcon}
                width={112}
                height={112}
                alt=""
                className="shrink-0"
              />
              <h2 className="w-full text-center font-display text-2xl font-medium text-purple-light">
                Got it, thank you
              </h2>
              <p className="w-full text-center font-body text-base text-purple-light/70">
                We're building Live Palmistry with you — your feedback makes it
                better
              </p>
            </div>
            <button
              type="button"
              onClick={() => closeReviewModal("continue")}
              className={CTA_CLASS}
            >
              Continue
            </button>
          </>
        ) : null}
      </div>
    </div>
  )
}
